import { DollarSign } from "lucide-react";
import { useCallback, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { StatCard } from "@/components/shared/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { formatCurrency, generateColorScale } from "@/lib/chart-helpers";

// Backend response shape from metrics.cost → loadCostUsageSummary
type CostDailyEntry = {
  date: string;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  totalCost: number;
  inputCost: number;
  outputCost: number;
  cacheReadCost: number;
  cacheWriteCost: number;
};

type CostTotals = {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  totalCost: number;
  inputCost: number;
  outputCost: number;
  cacheReadCost: number;
  cacheWriteCost: number;
};

type CostSummary = {
  daily: CostDailyEntry[];
  totals: CostTotals;
  days: number;
};

type AgentSummary = {
  totalRuns: number;
  totalCost: number;
  totalTokens: number;
  byModel: Record<string, { runs: number; cost: number; tokens: number }>;
};

type CostsData = {
  cost: CostSummary;
  agents: AgentSummary;
};

const PIE_COLORS = generateColorScale(8);
const TOKEN_COLORS = { input: "#6366f1", output: "#22c55e", cacheRead: "#f59e0b", cacheWrite: "#ef4444" };

function formatTokens(n: number): string {
  if (n >= 1_000_000) {return `${(n / 1_000_000).toFixed(1)}M`;}
  if (n >= 1_000) {return `${(n / 1_000).toFixed(1)}K`;}
  return String(n);
}

function costInCents(dollars: number): string {
  return formatCurrency(Math.round(dollars * 100));
}

export function Costs() {
  const { rpc, state } = useGatewayRpc();
  const [days, setDays] = useState(30);

  const fetcher = useCallback(async (): Promise<CostsData> => {
    const [costRes, agentRes] = await Promise.all([
      rpc<CostSummary>("metrics.cost", { days }),
      rpc<{ summary: AgentSummary }>("metrics.agents", { since: Date.now() - days * 86_400_000 }),
    ]);
    return { cost: costRes, agents: agentRes.summary };
  }, [rpc, days]);

  const { data, loading } = usePolling(fetcher, 30_000, { enabled: state === "connected" });

  const totals = data?.cost.totals;
  const daily = data?.cost.daily ?? [];

  // Today's cost
  const today = new Date().toISOString().slice(0, 10);
  const todayCost = daily.find((d) => d.date === today)?.totalCost ?? 0;

  // 7-day cost
  const sevenDaysAgo = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  const weekCost = daily.filter((d) => d.date >= sevenDaysAgo).reduce((s, d) => s + d.totalCost, 0);

  // Model breakdown from agents summary
  const modelPie = Object.entries(data?.agents.byModel ?? {})
    .map(([model, v]) => ({ name: model.split("-").slice(0, 2).join("-"), cost: v.cost, tokens: v.tokens, runs: v.runs }))
    .filter((m) => m.cost > 0 || m.tokens > 0)
    .sort((a, b) => b.cost - a.cost);

  // Token breakdown bar data
  const tokenBar = daily.slice(-14).map((d) => ({
    date: d.date.slice(5), // MM-DD
    input: d.input,
    output: d.output,
    cacheRead: d.cacheRead,
    cacheWrite: d.cacheWrite,
  }));

  // Daily cost bar data
  const costBar = daily.slice(-14).map((d) => ({
    date: d.date.slice(5),
    cost: d.totalCost,
  }));

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Costs & Usage</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Costs & Usage</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Today" value={costInCents(todayCost)} sparkColor="var(--color-healthy)" />
        <StatCard title="7-Day" value={costInCents(weekCost)} sparkColor="var(--color-warning)" />
        <StatCard title={`${days}-Day Total`} value={costInCents(totals?.totalCost ?? 0)} />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tokens</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-semibold tabular-nums">{formatTokens(totals?.totalTokens ?? 0)}</span>
          </CardContent>
        </Card>
      </div>

      {/* Daily Cost Bar Chart + Model Breakdown */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <DollarSign className="h-4 w-4" /> Daily Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costBar.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No cost data</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={costBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted)" opacity={0.2} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted)" tickFormatter={(v: number) => `$${v.toFixed(2)}`} />
                  <Tooltip formatter={(v: number) => [`$${v.toFixed(4)}`, "Cost"]} />
                  <Bar dataKey="cost" fill="#6366f1" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cost by Model</CardTitle>
          </CardHeader>
          <CardContent>
            {modelPie.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No model data</p>
            ) : (
              <div className="flex items-start gap-4">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie data={modelPie} dataKey="cost" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                      {modelPie.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [`$${v.toFixed(4)}`]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1 text-xs">
                  {modelPie.map((m, i) => (
                    <div key={m.name} className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="flex-1 truncate">{m.name}</span>
                      <span className="tabular-nums text-muted-foreground">{costInCents(m.cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Token Breakdown Stacked Bar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Token Breakdown (14 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {tokenBar.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No token data</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={tokenBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted)" opacity={0.2} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted)" tickFormatter={formatTokens} />
                  <Tooltip formatter={(v: number, name: string) => [formatTokens(v), name]} />
                  <Bar dataKey="input" stackId="tokens" fill={TOKEN_COLORS.input} name="Input" />
                  <Bar dataKey="output" stackId="tokens" fill={TOKEN_COLORS.output} name="Output" />
                  <Bar dataKey="cacheRead" stackId="tokens" fill={TOKEN_COLORS.cacheRead} name="Cache Read" />
                  <Bar dataKey="cacheWrite" stackId="tokens" fill={TOKEN_COLORS.cacheWrite} name="Cache Write" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                {Object.entries(TOKEN_COLORS).map(([key, color]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                    {key === "cacheRead" ? "Cache Read" : key === "cacheWrite" ? "Cache Write" : key.charAt(0).toUpperCase() + key.slice(1)}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cost Breakdown Table */}
      {totals && (totals.totalCost > 0 || totals.totalTokens > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4">Category</th>
                  <th className="pb-2 pr-4 text-right">Tokens</th>
                  <th className="pb-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Input", tokens: totals.input, cost: totals.inputCost },
                  { label: "Output", tokens: totals.output, cost: totals.outputCost },
                  { label: "Cache Read", tokens: totals.cacheRead, cost: totals.cacheReadCost },
                  { label: "Cache Write", tokens: totals.cacheWrite, cost: totals.cacheWriteCost },
                ].map((row) => (
                  <tr key={row.label} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-4 font-medium">{row.label}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-500 dark:text-slate-400">{formatTokens(row.tokens)}</td>
                    <td className="py-2 text-right tabular-nums">{costInCents(row.cost)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="pt-2">Total</td>
                  <td className="pt-2 text-right tabular-nums">{formatTokens(totals.totalTokens)}</td>
                  <td className="pt-2 text-right tabular-nums">{costInCents(totals.totalCost)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <p className="text-center text-sm text-slate-400">Loading cost data...</p>
      )}
    </div>
  );
}
